/**
 * Command palette for quick navigation and actions (⌘K)
 */

import React, { useEffect, useState } from "react";
import { Modal, Input, List, Typography, Space, Tag } from "antd";
import {
  SearchOutlined,
  BankOutlined,
  UserOutlined,
  DollarOutlined,
  PhoneOutlined,
  MailOutlined,
  PlusOutlined,
  BarChartOutlined,
  ShoppingCartOutlined,
  SolutionOutlined,
  FileTextOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useGo } from "@refinedev/core";

const { Text } = Typography;

interface Command {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  category: "navigation" | "action" | "search";
  shortcut?: string;
  action: () => void;
}

export const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const go = useGo();

  // Listen for keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Define available commands
  const commands: Command[] = [
    // Navigation
    {
      id: "go-accounts",
      label: "Go to Accounts",
      icon: <BankOutlined />,
      category: "navigation",
      shortcut: "G A",
      action: () => {
        go({ to: "/accounts" });
        setOpen(false);
      },
    },
    {
      id: "go-contacts",
      label: "Go to Contacts",
      icon: <UserOutlined />,
      category: "navigation",
      shortcut: "G C",
      action: () => {
        go({ to: "/contacts" });
        setOpen(false);
      },
    },
    {
      id: "go-opportunities",
      label: "Go to Opportunities",
      icon: <DollarOutlined />,
      category: "navigation",
      shortcut: "G O",
      action: () => {
        go({ to: "/opportunities" });
        setOpen(false);
      },
    },
    {
      id: "go-pipeline",
      label: "View Pipeline",
      icon: <BarChartOutlined />,
      category: "navigation",
      action: () => {
        go({ to: "/pipeline" });
        setOpen(false);
      },
    },
    {
      id: "go-sales-orders",
      label: "Go to Sales Orders",
      icon: <ShoppingCartOutlined />,
      category: "navigation",
      shortcut: "G S",
      action: () => {
        go({ to: "/sales-orders" });
        setOpen(false);
      },
    },
    {
      id: "go-offers",
      label: "Go to Quotes",
      icon: <SolutionOutlined />,
      category: "navigation",
      shortcut: "G Q",
      action: () => {
        go({ to: "/offers" });
        setOpen(false);
      },
    },
    {
      id: "go-invoices",
      label: "Go to Invoices",
      icon: <FileTextOutlined />,
      category: "navigation",
      shortcut: "G I",
      action: () => {
        go({ to: "/invoices" });
        setOpen(false);
      },
    },
    // Detail modules (hidden from toolbar, accessible via ⌘K)
    {
      id: "go-sales-order-details",
      label: "Sales Order Lines",
      description: "View all order line items",
      icon: <UnorderedListOutlined />,
      category: "navigation",
      action: () => {
        go({ to: "/sales-order-details" });
        setOpen(false);
      },
    },
    {
      id: "go-offer-details",
      label: "Quote Lines",
      description: "View all quote line items",
      icon: <UnorderedListOutlined />,
      category: "navigation",
      action: () => {
        go({ to: "/offer-details" });
        setOpen(false);
      },
    },
    {
      id: "go-invoice-details",
      label: "Invoice Lines",
      description: "View all invoice line items",
      icon: <UnorderedListOutlined />,
      category: "navigation",
      action: () => {
        go({ to: "/invoice-details" });
        setOpen(false);
      },
    },
    // Actions
    {
      id: "new-account",
      label: "New Account",
      description: "Create a new account",
      icon: <PlusOutlined />,
      category: "action",
      shortcut: "N A",
      action: () => {
        go({ to: "/accounts/create" });
        setOpen(false);
      },
    },
    {
      id: "new-contact",
      label: "New Contact",
      description: "Create a new contact",
      icon: <PlusOutlined />,
      category: "action",
      shortcut: "N C",
      action: () => {
        go({ to: "/contacts/create" });
        setOpen(false);
      },
    },
    {
      id: "new-opportunity",
      label: "New Opportunity",
      description: "Create a new opportunity",
      icon: <PlusOutlined />,
      category: "action",
      shortcut: "N O",
      action: () => {
        go({ to: "/opportunities/create" });
        setOpen(false);
      },
    },
    {
      id: "log-call",
      label: "Log Call",
      description: "Log a phone call",
      icon: <PhoneOutlined />,
      category: "action",
      shortcut: "L C",
      action: () => {
        // TODO: Open log call modal
        setOpen(false);
      },
    },
    {
      id: "send-email",
      label: "Send Email",
      description: "Compose and send email",
      icon: <MailOutlined />,
      category: "action",
      shortcut: "L E",
      action: () => {
        // TODO: Open email composer
        setOpen(false);
      },
    },
  ];

  // Filter commands based on search
  const filteredCommands = commands.filter((cmd) => {
    const searchLower = search.toLowerCase();
    return (
      cmd.label.toLowerCase().includes(searchLower) ||
      cmd.description?.toLowerCase().includes(searchLower)
    );
  });

  // Group by category
  const groupedCommands = {
    navigation: filteredCommands.filter((c) => c.category === "navigation"),
    action: filteredCommands.filter((c) => c.category === "action"),
    search: filteredCommands.filter((c) => c.category === "search"),
  };

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
      closable={false}
      width={500}
      style={{ top: 100 }}
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0" }}>
        <Input
          placeholder="Type a command or search..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          bordered={false}
          autoFocus
          size="large"
        />
      </div>

      <div style={{ maxHeight: 400, overflowY: "auto" }}>
        {/* Navigation */}
        {groupedCommands.navigation.length > 0 && (
          <CommandGroup
            title="Navigation"
            commands={groupedCommands.navigation}
          />
        )}

        {/* Actions */}
        {groupedCommands.action.length > 0 && (
          <CommandGroup title="Actions" commands={groupedCommands.action} />
        )}

        {filteredCommands.length === 0 && (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Text type="secondary">No commands found</Text>
          </div>
        )}
      </div>

      <div
        style={{
          padding: "8px 16px",
          borderTop: "1px solid #f0f0f0",
          background: "#fafafa",
        }}
      >
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <Tag>↵</Tag> to select
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <Tag>↑↓</Tag> to navigate
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <Tag>esc</Tag> to close
          </Text>
        </Space>
      </div>
    </Modal>
  );
};

// Command group component
const CommandGroup: React.FC<{ title: string; commands: Command[] }> = ({
  title,
  commands,
}) => {
  return (
    <div>
      <div
        style={{
          padding: "8px 16px",
          background: "#fafafa",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
          {title}
        </Text>
      </div>
      <List
        size="small"
        dataSource={commands}
        renderItem={(cmd) => (
          <List.Item
            onClick={cmd.action}
            style={{
              padding: "8px 16px",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f5f5f5";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Space>
                {cmd.icon}
                <div>
                  <Text>{cmd.label}</Text>
                  {cmd.description && (
                    <Text
                      type="secondary"
                      style={{ display: "block", fontSize: 12 }}
                    >
                      {cmd.description}
                    </Text>
                  )}
                </div>
              </Space>
              {cmd.shortcut && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {cmd.shortcut}
                </Text>
              )}
            </Space>
          </List.Item>
        )}
      />
    </div>
  );
};
